import { analytics } from 'lib/analytics/analytics';
import { allowsDataLayer, getConsentGate } from 'lib/consent/gate';
import { emit } from 'lib/analytics/fanout';
import { trackTripleWhaleAddToCart } from 'lib/analytics/tripleWhale';
import { pushToDataLayer } from './utils';

type Scalar = string | number | boolean | null | undefined;
export type FunnelEventPayload = Record<string, Scalar>;

export type EcommerceItem = {
  item_id: string;
  item_name: string;
  item_variant?: string;
  item_category?: string;
  price: number;
  quantity: number;
};

export type EcommercePayload = {
  currency: string;
  value: number;
  items: EcommerceItem[];
};

type DataLayerWindow = Window & { dataLayer?: Record<string, unknown>[] };

const stripUndefined = <T extends Record<string, unknown>>(obj: T): T =>
  Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== undefined),
  ) as T;

/** `pushToDataLayer` is a no-op until GTM has created `window.dataLayer`;
 *  create it first so early events are not lost. */
export function pushDataLayerEvent(
  event: string,
  payload?: Record<string, unknown>,
): void {
  if (typeof window === 'undefined') return;
  if (!allowsDataLayer()) return;
  (window as DataLayerWindow).dataLayer ??= [];
  pushToDataLayer(event, payload ? stripUndefined(payload) : undefined);
}

/** `ringBuyCtaClick` → `ring_buy_cta_click`; GA4 standard names pass through. */
export const toSnakeCase = (event: string): string =>
  event.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase();

/** Every custom event goes to both sinks: GTM dataLayer under its camelCase
 *  name and Vercel Analytics under the snake_case form of the same name.
 *  On the next stack both sinks go through one emit, after alias and volume
 *  guards. GTM still needs a trigger per event. */
export function trackEvent(
  gtmEvent: string,
  payload: FunnelEventPayload = {},
): void {
  const clean = stripUndefined(payload);
  if (getConsentGate().stack === 'next') {
    emit({
      name: toSnakeCase(gtmEvent),
      gtmName: gtmEvent,
      props: clean,
    });
    return;
  }
  pushDataLayerEvent(gtmEvent, clean);
  analytics.track(toSnakeCase(gtmEvent), clean);
}

/** GTM dataLayer (camelCase name) plus, when given, Vercel Analytics
 *  (explicit snake_case name) for events whose Vercel name differs. */
export function trackFunnelEvent(
  gtmEvent: string | null,
  vercelEvent: string | null,
  payload: FunnelEventPayload = {},
): void {
  const clean = stripUndefined(payload);
  if (getConsentGate().stack === 'next') {
    const name = vercelEvent ?? (gtmEvent ? toSnakeCase(gtmEvent) : '');
    if (!name) return;
    emit({
      name,
      gtmName: gtmEvent ?? undefined,
      props: clean,
    });
    return;
  }
  if (gtmEvent) pushDataLayerEvent(gtmEvent, clean);
  if (vercelEvent) analytics.track(vercelEvent, clean);
}

/** GA4 recommended ecommerce shape: clear the previous object, then push. */
export function pushEcommerceEvent(
  event: 'view_item' | 'add_to_cart' | 'begin_checkout',
  ecommerce: EcommercePayload,
  extra: FunnelEventPayload = {},
): void {
  if (typeof window === 'undefined') return;
  if (event === 'add_to_cart') trackTripleWhaleAddToCart(ecommerce.items);
  const extraClean = stripUndefined(extra);
  if (getConsentGate().stack === 'next') {
    emit({
      name: event,
      props: extraClean,
      ecommerce,
    });
    return;
  }
  if (!allowsDataLayer()) return;
  const w = window as DataLayerWindow;
  w.dataLayer ??= [];
  w.dataLayer.push({ ecommerce: null });
  pushToDataLayer(event, { ecommerce, ...extraClean });
}

// Cart drawer events (shared cart, every product).
export const CART_GTM_EVENTS = {
  DRAWER_OPENED: 'cartDrawerOpened',
  DRAWER_CLOSED: 'cartDrawerClosed',
  LINE_REMOVED: 'cartLineRemoved',
  QUANTITY_CHANGED: 'cartQuantityChanged',
  GIFT_TOGGLED: 'cartGiftToggled',
  GIFT_NOTE_SAVED: 'cartGiftNoteSaved',
  GIFT_STEP_CONTINUE: 'cartGiftStepContinue',
  PRICE_BREAKUP_TOGGLED: 'cartPriceBreakupToggled',
  ADDON_LEARN_MORE_ADD: 'cartAddonLearnMoreAdd',
  ADDON_CAROUSEL_NAV: 'cartAddonCarouselNav',
  CHECKOUT_CLICK: 'cartCheckoutClick',
  CHECKOUT_FAILED: 'cartCheckoutFailed',
} as const;

export const CART_ANALYTICS_EVENTS = {
  CHECKOUT_CLICK: 'cart_checkout_click',
  BEGIN_CHECKOUT: 'cart_begin_checkout',
  CHECKOUT_FAILED: 'cart_checkout_failed',
} as const;
